import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { downloadReport } from "@/lib/report";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Loader2, Sparkles, Download } from "lucide-react";
import { AIText } from "@/components/AIText";

export default function MissedPayment() {
  const { t, lang } = useI18n();
  const [policies, setPolicies] = useState([]);
  const [docs, setDocs] = useState([]);
  const [source, setSource] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/policies").then((r) => setPolicies(r.data));
    api.get("/documents").then((r) => setDocs(r.data.filter((d) => d.analysis)));
  }, []);

  const analyze = async () => {
    setLoading(true); setAnswer("");
    const [kind, id] = source ? source.split(":") : ["", ""];
    try {
      const payload = kind === "doc" ? { document_id: id, language: lang } : kind === "pol" ? { policy_id: id, language: lang } : { language: lang };
      const r = await api.post("/missed-payment/analyze", payload);
      setAnswer(r.data.answer);
    } catch { toast.error("Analysis failed"); } finally { setLoading(false); }
  };

  const exportPdf = () => downloadReport({ title: "Missed Payment Guidance", language: lang,
    sections: [{ heading: "What to know", body: answer }] });

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><Clock className="h-7 w-7 text-brandblue" /> {t("nav_missed_payment")}</h1>
      <p className="mt-1 text-slate-500">{t("missed_sub")}</p>

      <div className="mt-6 rounded-3xl bg-white border border-slate-100 card-shadow p-6 max-w-2xl">
        <Label>{t("missed_which_policy")}</Label>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger data-testid="missed-source" className="mt-1.5 rounded-xl"><SelectValue placeholder={t("missed_general")} /></SelectTrigger>
          <SelectContent>
            {policies.map((p) => <SelectItem key={p.id} value={`pol:${p.id}`}>{t("cat_" + (p.category || "").toLowerCase())} · {p.carrier || "—"} ({t("wallet_tag")})</SelectItem>)}
            {docs.map((d) => <SelectItem key={d.id} value={`doc:${d.id}`}>{d.original_filename} ({t("document_tag")})</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="missed-analyze-btn" onClick={analyze} disabled={loading} className="mt-4 rounded-full brand-gradient text-white">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("analyzing")}</> : <><Sparkles className="h-4 w-4 mr-2" />{t("missed_explain_options")}</>}
        </Button>
      </div>

      {answer && (
        <div className="mt-6 rounded-3xl bg-white border border-slate-100 card-shadow p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-navy flex items-center gap-2"><Sparkles className="h-5 w-5 text-brandteal" /> {t("missed_what_to_know")}</h2>
            <Button data-testid="missed-export" variant="outline" onClick={exportPdf} className="rounded-full"><Download className="h-4 w-4 mr-2" />{t("export_pdf")}</Button>
          </div>
          <div className="mt-3"><AIText>{answer}</AIText></div>
        </div>
      )}
    </AppLayout>
  );
}
